import { COMPONENT_FACTORY_CONFIG_INPUT_COMPONENTS } from '@core/components/component-factory/component-factory-config-input.registry';
import type { ConfigComponentContract } from '@shared/utils/content-builder/config-component-contract';
import { computedResolvedConfig } from '@shared/utils/content-builder/block-config-computed-resolver';
import type { ComoCreditBalanceCardBlockConfig } from '@front-platform/content-builder';
import { comoCreditBalanceCardBlockResolveConfig } from '@front-platform/content-builder';
import { Subject } from 'rxjs';
import { takeUntil, tap } from 'rxjs/operators';
import { Component, OnDestroy, OnInit, inject, input } from '@angular/core';
import type { ComoCreditBalanceCardBlockScenarioContent } from '@front-platform/content-builder';
import { COMO_CREDIT_BALANCE_CARD_BLOCK_COMPONENT } from '@core/services/cb2-preview/cb2-preview-bloc-types';
import { Cb2PreviewStateService } from '@core/services/cb2-preview/cb2-preview-state.service';
import { previewScenarioData } from '@core/services/cb2-preview/preview-scenario-data.util';
import { previewScenarioForBloc$ } from '@core/services/cb2-preview/preview-scenario-for-bloc.util';

import { ComoService } from '@core/services/como/como.service';
import { ModalsService } from '@core/services/modals/modals.service';

import { ComoAsset, ComoUser } from '@core/models/como.model';
import { BasketStoreSelector } from '@common/selectors/basket.selector';
import { ComoUseCreditBalanceModalComponent } from '@shared/modals/como/como-use-credit-balance-modal/como-use-credit-balance-modal.component';
export interface ComoCreditBalanceCardBlockRuntimeConfig {
  'points_label'?: string;
  'credit_points_title'?: string;
  'loyalty_points_title'?: string;
}

export type ComoCreditBalanceCardBlockConfigInput = Partial<
  Omit<ComoCreditBalanceCardBlockConfig, keyof ComoCreditBalanceCardBlockRuntimeConfig> &
    ComoCreditBalanceCardBlockRuntimeConfig
>;
export type ComoCreditBalanceCardBlockResolvedConfig = Omit<
  ComoCreditBalanceCardBlockConfig,
  keyof ComoCreditBalanceCardBlockRuntimeConfig
> &
  Partial<ComoCreditBalanceCardBlockRuntimeConfig>;
export interface ComoCreditBalanceCardBlockContract extends ConfigComponentContract<
  ComoCreditBalanceCardBlockConfigInput,
  ComoCreditBalanceCardBlockResolvedConfig
> {}

@Component({
  selector: 'app-como-credit-balance-card-block',
  templateUrl: './como-credit-balance-card-block.component.html',
  standalone: false,
})
export class ComoCreditBalanceCardBlockComponent
  implements OnInit, OnDestroy, ComoCreditBalanceCardBlockContract
{
  readonly config = input<ComoCreditBalanceCardBlockConfigInput>({});
  readonly resolvedConfig = computedResolvedConfig<
    ComoCreditBalanceCardBlockConfig,
    ComoCreditBalanceCardBlockRuntimeConfig
  >(comoCreditBalanceCardBlockResolveConfig, () => ({ config: this.config() }));
  // TODO: Remove french values and replace them by i18n values
  get pointsLabel(): string {
    return this.resolvedConfig()['points_label'] as unknown as string;
  }
  get creditPointsTitle(): string {
    return this.resolvedConfig()['credit_points_title'] as unknown as string;
  }
  get loyaltyPointsTitle(): string {
    return this.resolvedConfig()['loyalty_points_title'] as unknown as string;
  }

  public comoUser?: ComoUser | null;
  /** Preview has no basket; the card must stay visible when a scenario provides a balance. */
  forceVisibleInPreview = false;

  private readonly cb2PreviewState = inject(Cb2PreviewStateService);
  private readonly previewScenario$ = previewScenarioForBloc$(
    COMO_CREDIT_BALANCE_CARD_BLOCK_COMPONENT,
  );

  basketId$ = this.basketSelector.selectId;

  private readonly destroyed$ = new Subject<boolean>();

  constructor(
    private readonly comoService: ComoService,
    private basketSelector: BasketStoreSelector,
    private readonly modalsService: ModalsService,
  ) {}

  ngOnInit(): void {
    if (this.cb2PreviewState.isActive()) {
      this.forceVisibleInPreview = true;
      this.previewScenario$
        .pipe(
          takeUntil(this.destroyed$),
          tap(scenario => {
            const content = previewScenarioData<ComoCreditBalanceCardBlockScenarioContent>(
              scenario,
              COMO_CREDIT_BALANCE_CARD_BLOCK_COMPONENT,
            );
            this.comoUser = (content?.comoUser as ComoUser | null | undefined) ?? null;
          }),
        )
        .subscribe();
      return;
    }

    this.comoService.loadComoUser();
    this.comoService
      .getComoUser$()
      .pipe(
        takeUntil(this.destroyed$),
        tap(comoUser => (this.comoUser = comoUser)),
      )
      .subscribe();
  }

  ngOnDestroy(): void {
    this.modalsService.close(ComoUseCreditBalanceModalComponent.handle);
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }

  onAssetClick(_$event: ComoAsset): void {}

  useCreditBalance(): void {
    this.modalsService.open(ComoUseCreditBalanceModalComponent.handle);
  }
}
COMPONENT_FACTORY_CONFIG_INPUT_COMPONENTS.set(ComoCreditBalanceCardBlockComponent, {
  'keyAliases': {},
});
